import {
  ActiveMilitaryPersonalInfoRequest,
  ActiveMilitaryPersonalInfoViewModel,
  AgePersonalInfoRequest,
  AgePersonalInfoViewModel,
  CollectIdentifierRequest,
  CollectIdentifierViewModel,
  CollectPersonalInfoRequest,
  CollectPersonalInfoViewModel,
  CompleteAuthenticationRequest,
  CompleteAuthenticationViewModel,
  DriverLicensePersonalInfoRequest,
  DriverLicensePersonalInfoViewModel,
  EmailLoopCollectOrganizationEmailViewModel,
  EmailLoopCollectOrganizationRequest,
  EmailLoopVerificationRequest,
  EmailLoopViewModel,
  EmploymentPersonalInfoRequest,
  EmploymentPersonalInfoViewModel,
  FirstResponderPersonalInfoRequest,
  FirstResponderPersonalInfoViewModel,
  GeneralIdentityPersonalInfoRequest,
  GeneralIdentityPersonalInfoViewModel,
  HybridIdentityPersonalInfoRequest,
  HybridIdentityPersonalInfoViewModel,
  IDCheckLoopVerificationRequest,
  InactiveMilitaryPersonalInfoRequest,
  InactiveMilitaryPersonalInfoViewModel,
  LicensedProfessionalPersonalInfoRequest,
  LicensedProfessionalPersonalInfoViewModel,
  LowIncomePersonalInfoRequest,
  LowIncomePersonalInfoViewModel,
  MedicalProfessionalPersonalInfoRequest,
  MedicalProfessionalPersonalInfoViewModel,
  MemberPersonalInfoRequest,
  MemberPersonalInfoViewModel,
  MilitaryStatusRequest,
  MoverPersonalInfoRequest,
  MoverPersonalInfoViewModel,
  OverrideVerificationRequest,
  OverrideViewModel,
  SMSLoopVerificationRequest,
  SMSLoopViewModel,
  SeniorPersonalInfoRequest,
  SeniorPersonalInfoViewModel,
  SocialSecurityRequest,
  SocialSecurityViewModel,
  StudentPersonalInfoRequest,
  StudentPersonalInfoViewModel,
  TeacherPersonalInfoRequest,
  TeacherPersonalInfoViewModel,
} from "../types/types";
import { logger } from "../utils/logger/logger";

// StudentPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const studentViewModelToRequest = (
  viewModel: StudentPersonalInfoViewModel,
): StudentPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
  cvecNumber: viewModel.cvecNumber,
});

// TeacherPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const teacherViewModelToRequest = (
  viewModel: TeacherPersonalInfoViewModel,
): TeacherPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  postalCode: viewModel.postalCode,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

export const memberViewModelToRequest = (
  viewModel: MemberPersonalInfoViewModel,
): MemberPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  memberId: viewModel.memberId,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  postalCode: viewModel.postalCode,
  country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
});

const seniorViewModelToRequest = (
  viewModel: SeniorPersonalInfoViewModel,
): SeniorPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  email: viewModel.email,
  postalCode: viewModel.postalCode,
  phoneNumber: viewModel.phoneNumber,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const ageViewModelToRequest = (viewModel: AgePersonalInfoViewModel): AgePersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  email: viewModel.email,
  postalCode: viewModel.postalCode !== "" ? viewModel.postalCode : undefined,
  phoneNumber: viewModel.phoneNumber !== "" ? viewModel.phoneNumber : undefined,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
  city: viewModel.city !== "" ? viewModel.city : undefined,
  address1: viewModel.address1 !== "" ? viewModel.address1 : undefined,
  metadata: viewModel.metadata,
});

// ActiveMilitaryPersonalInfoViewModel contains multiple keys we don't want so we can't return the full viewModel as the request body
const militaryStatusViewModelToRequest = (
  viewModel: ActiveMilitaryPersonalInfoViewModel,
): MilitaryStatusRequest => ({
  status: viewModel.status,
});

// ActiveMilitaryPersonalInfoViewModel contains 'status' which we don't want in the request so we can't return the full viewModel
const activeMilitaryViewModelToRequest = (
  viewModel: ActiveMilitaryPersonalInfoViewModel,
): ActiveMilitaryPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  activeDutyStartDate: viewModel.activeDutyStartDate || undefined,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
  metadata: viewModel.metadata,
});

// InactiveMilitaryPersonalInfoViewModel contains 'status' which we don't want in the request so we can't return the full viewModel
const inactiveMilitaryViewModelToRequest = (
  viewModel: InactiveMilitaryPersonalInfoViewModel,
): InactiveMilitaryPersonalInfoRequest => {
  const requestObj = {
    firstName: viewModel.firstName,
    lastName: viewModel.lastName,
    birthDate: viewModel.birthDate,
    activeDutyStartDate: viewModel.activeDutyStartDate || undefined,
    email: viewModel.email,
    phoneNumber: viewModel.phoneNumber,
    organization: viewModel.organization,
    dischargeDate: viewModel.dischargeDate,
    deviceFingerprintHash: viewModel.deviceFingerprintHash,
    externalUserId: viewModel.externalUserId,
    locale: viewModel.localeChoice.value,
    country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
    metadata: viewModel.metadata,
  };
  // these fields are optional AND conditionally shown based on program config
  const optionalFields = ["city", "state", "postalCode", "address1"];
  Object.keys(viewModel).forEach((key) => {
    if (optionalFields.includes(key)) {
      requestObj[key] = viewModel[key];
    }
  });
  return requestObj;
};

export const firstResponderViewModelToRequest = (
  viewModel: FirstResponderPersonalInfoViewModel,
): FirstResponderPersonalInfoRequest => {
  const request: FirstResponderPersonalInfoRequest = {
    firstName: viewModel.firstName,
    lastName: viewModel.lastName,
    birthDate: viewModel.birthDate,
    email: viewModel.email,
    status: viewModel.status,
    postalCode: viewModel.postalCode,
    phoneNumber: viewModel.phoneNumber,
    organization: viewModel.organization,
    deviceFingerprintHash: viewModel.deviceFingerprintHash,
    externalUserId: viewModel.externalUserId,
    locale: viewModel.localeChoice.value,
    country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
    metadata: viewModel.metadata,
  };

  const hasStatuses =
    viewModel.statuses && Array.isArray(viewModel.statuses) && viewModel.statuses.length > 0;
  if (hasStatuses) {
    request.statuses = viewModel.statuses;
    delete request.status; // API prefers the statuses field only
  }

  if (!(viewModel.status || hasStatuses)) {
    logger.error("Either status or statuses must be provided on First Responder view model");
  }

  return request;
};

const medicalProfessionalViewModelToRequest = (
  viewModel: MedicalProfessionalPersonalInfoViewModel,
): MedicalProfessionalPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  birthDate: viewModel.birthDate,
  status: viewModel.status,
  email: viewModel.email,
  postalCode: viewModel.postalCode,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
  country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
  memberId: viewModel.memberId,
});

const employmentViewModelToRequest = (
  viewModel: EmploymentPersonalInfoViewModel,
): EmploymentPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  postalCode: viewModel.postalCode,
  phoneNumber: viewModel.phoneNumber,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
  country: viewModel.countryChoice ? viewModel.countryChoice.value : undefined,
});

const driverLicenseViewModelToRequest = (
  viewModel: DriverLicensePersonalInfoViewModel,
): DriverLicensePersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  state: viewModel.state,
  driverLicenseNumber: viewModel.driverLicenseNumber,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const generalIdentityViewModelToRequest = (
  viewModel: GeneralIdentityPersonalInfoViewModel,
): GeneralIdentityPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  birthDate: viewModel.birthDate,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  postalCode: viewModel.postalCode,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const hybridIdentityViewModelToRequest = (
  viewModel: HybridIdentityPersonalInfoViewModel,
): HybridIdentityPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  birthDate: viewModel.birthDate,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  postalCode: viewModel.postalCode,
  driverLicenseNumber: viewModel.driverLicenseNumber,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const licensedProfessionalViewModelToRequest = (
  viewModel: LicensedProfessionalPersonalInfoViewModel,
): LicensedProfessionalPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  birthDate: viewModel.birthDate,
  postalCode: viewModel.postalCode,
  statuses: viewModel.statuses,
  organization: viewModel.organization,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const moverViewModelToRequest = (
  viewModel: MoverPersonalInfoViewModel,
): MoverPersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  statuses: viewModel.statuses,
  address1: viewModel.address1,
  postalCode: viewModel.postalCode,
  phoneNumber: viewModel.phoneNumber,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

export const lowIncomeViewModelToRequest = (
  viewModel: LowIncomePersonalInfoViewModel,
): LowIncomePersonalInfoRequest => ({
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  birthDate: viewModel.birthDate,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  postalCode: viewModel.postalCode,
  statuses: viewModel.statuses,
  organization:
    viewModel.statuses.includes("OTHER_GOVERNMENT_ASSISTANCE") ||
    viewModel.statuses.includes("COMMUNITY_ELIGIBILITY_PROVISION")
      ? viewModel.organization
      : undefined,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  ebtCardNumber: viewModel.statuses.includes("SNAP_BENEFITS") ? viewModel.ebtCardNumber : undefined,
  locale: viewModel.localeChoice.value,
  metadata: viewModel.metadata,
});

const smsLoopViewModelToRequest = (viewModel: SMSLoopViewModel): SMSLoopVerificationRequest => ({
  smsCode: viewModel.smsCode,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
});

const emailLoopViewModelToRequest = (
  viewModel: EmailLoopViewModel,
): EmailLoopVerificationRequest => ({
  emailToken: viewModel.emailToken,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
});

const emailLoopCollectOrganizationEmailViewModelToRequest = (
  viewModel: EmailLoopCollectOrganizationEmailViewModel,
): EmailLoopCollectOrganizationRequest => ({
  emailAddress: viewModel.organizationEmail,
});

const idCheckLoopViewModelToRequest = (): IDCheckLoopVerificationRequest => ({});

const overrideViewModelToRequest = (viewModel: OverrideViewModel): OverrideVerificationRequest => ({
  overrideCode: viewModel.overrideCode,
});

const socialSecurityViewModelToRequest = (
  viewModel: SocialSecurityViewModel,
): SocialSecurityRequest => {
  if (
    typeof viewModel.socialSecurityNumber !== "string" ||
    viewModel.socialSecurityNumber.length < 1
  ) {
    throw new Error("Poorly formed social security number, unable to submit");
  }

  const socialSecurityNumber = Number.parseInt(viewModel.socialSecurityNumber, 10); // Server wants an integer
  const requestBody: SocialSecurityRequest = { socialSecurityNumber };
  return requestBody;
};

const collectIdentifierRequest = (
  viewModel: CollectIdentifierViewModel,
): CollectIdentifierRequest => ({
  email: viewModel.email,
});

const completeAuthenticationRequest = (
  viewModel: CompleteAuthenticationViewModel,
): CompleteAuthenticationRequest => ({
  authenticationToken: viewModel.authenticationCode,
});

const collectPersonalInfoRequest = (
  viewModel: CollectPersonalInfoViewModel,
): CollectPersonalInfoRequest => ({
  driverLicenseNumber: viewModel.driverLicenseNumber,
  memberId: viewModel.memberId,
  postalCode: viewModel.postalCode,
  ebtCardNumber: viewModel.ebtCardNumber,
  birthDate: viewModel.birthDate,
  socialSecurityNumber: viewModel.socialSecurityNumber,
  dischargeDate: viewModel.dischargeDate,
  activeDutyStartDate: viewModel.activeDutyStartDate,
  country: viewModel.countryChoice.value,
  firstName: viewModel.firstName,
  lastName: viewModel.lastName,
  email: viewModel.email,
  phoneNumber: viewModel.phoneNumber,
  metadata: viewModel.metadata,
  deviceFingerprintHash: viewModel.deviceFingerprintHash,
  externalUserId: viewModel.externalUserId,
  address1: viewModel.address1,
  city: viewModel.city,
  state: viewModel.state,
  organization: viewModel.organization,
  statuses: [viewModel.status],
});

export const stepToRequest = {
  collectStudentPersonalInfo: studentViewModelToRequest,
  collectSeniorPersonalInfo: seniorViewModelToRequest,
  collectAgePersonalInfo: ageViewModelToRequest,
  collectMilitaryStatus: militaryStatusViewModelToRequest,
  collectActiveMilitaryPersonalInfo: activeMilitaryViewModelToRequest,
  collectInactiveMilitaryPersonalInfo: inactiveMilitaryViewModelToRequest,
  collectFirstResponderPersonalInfo: firstResponderViewModelToRequest,
  collectMedicalProfessionalPersonalInfo: medicalProfessionalViewModelToRequest,
  collectMemberPersonalInfo: memberViewModelToRequest,
  collectEmployeePersonalInfo: employmentViewModelToRequest,
  collectTeacherPersonalInfo: teacherViewModelToRequest,
  collectSocialSecurityNumber: socialSecurityViewModelToRequest,
  collectDriverLicensePersonalInfo: driverLicenseViewModelToRequest,
  collectGeneralIdentityPersonalInfo: generalIdentityViewModelToRequest,
  collectHybridIdentityPersonalInfo: hybridIdentityViewModelToRequest,
  collectLicensedProfessionalPersonalInfo: licensedProfessionalViewModelToRequest,
  collectMoverPersonalInfo: moverViewModelToRequest,
  collectLowIncomePersonalInfo: lowIncomeViewModelToRequest,
  collectIdentifier: collectIdentifierRequest,
  completeAuthentication: completeAuthenticationRequest,
  collectPersonalInfo: collectPersonalInfoRequest,
  smsLoop: smsLoopViewModelToRequest,
  emailLoop: emailLoopViewModelToRequest,
  emailLoopCollectOrganizationEmail: emailLoopCollectOrganizationEmailViewModelToRequest,
  idCheckLoop: idCheckLoopViewModelToRequest,
  override: overrideViewModelToRequest,
  cancelSocialSecurityNumber: null,
  sso: null,
  docUpload: null,
};
